/* global THREE */
import 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { DragControls } from 'three/examples/jsm/controls/DragControls';
import Stats from 'stats.js'; // eslint-disable-line

import LayerHTML from '../misc/html/html.layer';

import Axis from '../axis';
import { callErrorNotification } from '@components/Notification';
// import svgToTinyDataUri from 'mini-svg-data-uri';
import { APP_TEXTS } from '@texts';
import html2canvas from 'html2canvas';

const zoom = 3;
const ORTHOGRAPHIC_CAMERA_FAR = 99999;

var fps = 60;
var now;
var then = Date.now();
var interval = 1000/fps;
var delta;

export default class ObjectView {

  static mode = {
    MODE_3D: '3D',
    MODE_2D: '2D',
    MODE_2D_EDITOR: 'MODE_2D_EDITOR'
  };

  static position = {
    TOP: 'top',
    BOTTOM: 'bottom',
    LEFT: 'left',
    RIGHT: 'right'
  };

  constructor(options = {}, container) {
    this.options = options;

    this.container = container;
    this.width = this.container.clientWidth -8; // resizer width
    this.height = this.container.clientHeight;

    this.htmlLayer = new LayerHTML();
    this.globalObject = new THREE.Object3D();

    this.options.mode = this.options.mode || ObjectView.mode.MODE_3D;

    THREE.Cache.enabled = true;

    this.scene = new THREE.Scene();

    this.scene.add(this.globalObject);

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(this.width, this.height);
    this.renderer.domElement.style.position = "fixed";
    this.renderer.setClearColor(0xffffff, 1);

    this.lightOn();

    this.setPerspectiveCamera();

    if (process.env.NODE_ENV === 'development') {
      this.stats = new Stats();
      this.stats.showPanel(0);

      this.container.appendChild(this.statsDomElement);
    }

    this.container.appendChild(this.domElement);

    this.createAxis();

    /// DEBUG
    // var debugAxis = new THREE.AxesHelper(350);
    // this.scene.add(debugAxis);
    /// ~DEBUG

    this.onContextLose();
  }

  onContextLose() {
    const ext = this.renderer.getContext().getExtension('WEBGL_lose_context');
    this.renderer.getContext().canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();

      setTimeout(() => {
        ext.restoreContext();
      });
    }, false);
    this.renderer.getContext().canvas.addEventListener('webglcontextrestored', () => {
      console.error('restore context');

      this.renderer.setClearColor(0xffffff, 1);
    }, false);
  }

  lightOn() {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    this.scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0xffffff, 0.8, 5000);
    pointLight.position.set(900, 600, 800);
    this.scene.add(pointLight);
  }

  setMode (mode) {
    this.options.mode = mode;

    if (this.options.mode === ObjectView.mode.MODE_2D) {
      if (this.object) {
        this.object.set2DMode();
      }

      this.setOrthographicCamera();

      this.createAxis(ObjectView.mode.MODE_2D);
    } else if (this.options.mode === ObjectView.mode.MODE_2D_EDITOR) {
      this.setOrthographicCamera();
      this.object.set2DEditorMode();
      this.createAxis(ObjectView.mode.MODE_2D);
    } else {
      if (this.object) {
        this.object.set3DMode();
      }

      this.setPerspectiveCamera();
      this.createAxis(ObjectView.mode.MODE_3D);
    }
  }

  setPerspectiveCamera() {
    this.camera = new THREE.PerspectiveCamera(
      45,
      this.width / this.height,
      1, 100000
    );

    this.camera.zoom = this.options.perspectiveCamera.zoom;

    this.setControls();
    this.setDragControls();
    this.setCameraPosition();

    this.controls.enableRotate = true;
  }

  setOrthographicCamera() {
    this.camera = new THREE.OrthographicCamera(
      this.width / -zoom, this.width / zoom,
      this.height / zoom, this.height / -zoom,
      1, ORTHOGRAPHIC_CAMERA_FAR
    );

    this.setControls();
    this.setDragControls();
    this.setCameraPosition();

    this.controls.enableRotate = false;
  }

  resetCamera() {
    this.controls.reset();
  }

  setCameraPosition() {
    if (this.camera) {
      if (this.options.mode === ObjectView.mode.MODE_3D) {
        const { position, target } = this.options.perspectiveCamera;

        if (position) {
          this.camera.position.set(position.x, position.y, position.z);
        }

        if (target) {
          this.controls.target.copy(target);
        }
      } else {
        this.camera.position.set(0, ORTHOGRAPHIC_CAMERA_FAR, 0);
      }

      this.controls.saveState();
    }
  }

  get mode() {
    return this.options.mode;
  }

  get nextMode() {
    return this.mode === ObjectView.mode.MODE_3D ? ObjectView.mode.MODE_2D : ObjectView.mode.MODE_3D;
  }

  setControls() {
    if (this.controls)
      this.controls.dispose();
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.minDistance = 300;
    this.controls.maxDistance = 5000;
    this.controls.minZoom = 0.1;
    this.controls.maxZoom = 8;
    this.controls.enableKeys = false;
  }

  setDragControls() {
    if (this.dragControls)
      this.dragControls.dispose();
    this.dragControls = new DragControls([], this.camera, this.renderer.domElement);
    this.dragControls.enabled = false;
    this.dragControls.addEventListener('dragstart', this.onDragControlEvent);
    this.dragControls.addEventListener('drag', this.onDragControlEvent);
    this.dragControls.addEventListener('dragend', this.onDragControlEvent);
    this.dragControls.addEventListener('hoveron', this.onDragControlEvent);
    this.dragControls.addEventListener('hoveroff', this.onDragControlEvent);
  }

  addObject(object) {
    this.object = object;
    // this.object.init(this.camera, this.controls);
    this.object.setOrientation();

    // Clear before add
    for (let i = this.globalObject.children.length - 1; i >= 0; i--) {
        this.globalObject.remove(this.globalObject.children[i]);
        this.htmlLayer.clear();
    }

    this.axis.setAxisOrientation(this.options.mode === ObjectView.mode.MODE_3D ? 
      this.options.axis.orientation3D : this.options.axis.orientation2D);

    this.globalObject.add(this.object.mesh);
    this.htmlLayer.addLayer(this.object.htmlLayer);
  }

  get objectName() {
    return this.object.options.name;
  }

  animate() {
    // VladP: Added a frame limiter to improve the performance
    // Render only 60 frames per second
    now = Date.now();
    delta = now - then;
    if (delta > interval){
      then = now - (delta % interval);

      if (process.env.NODE_ENV === 'development') {
        this.stats.begin();
      }
  
      // Allow mouse controls
      this.controls.update();
  
      this.renderer.render(this.scene, this.camera);
  
      if (this.axis) {
        this.axis.animate();
      }
  
      if (this.object) {
        this.object.render(this.camera, this.renderer, this.controls);

        // VladP: Three.js keeps references to objects in THREE.WebGLRenderLists even if they are already disposed.
        // https://github.com/mrdoob/three.js/pull/12464
        this.renderer.renderLists.dispose();
      }
      
      if (process.env.NODE_ENV === 'development') {
        this.stats.end();
      }
    }

    return window.requestAnimationFrame(this.animate.bind(this));
  }

  resize(width, height) {
    this.width = width;
    this.height = height;

    if (this.camera.isOrthographicCamera) {
      this.camera.left = this.width / -zoom;
      this.camera.right = this.width / zoom;
      this.camera.top = this.height / zoom;
      this.camera.bottom = this.height / -zoom;
    } else {
      this.camera.aspect = this.width / this.height;
    }

    this.camera.updateProjectionMatrix();

    this.renderer.setSize(this.width, this.height, true);
  }

  update(options) {
    this.options = options;

    this.controls.reset();

    this.setCameraPosition();
  }

  get domElement() {
    if (!this._viewHTMLElement) {
      this._viewHTMLElement = document.createElement('div');
      this._viewHTMLElement.appendChild(this.renderer.domElement);
      this._viewHTMLElement.appendChild(this.htmlLayer.domElement);
    }

    return this._viewHTMLElement;
  }

  get statsDomElement() {
    this.stats.dom.style.position = 'absolute';
    return this.stats.dom;
  }

  createAxis(mode = ObjectView.mode.MODE_3D) {
    let siblingCamera = null;

    if (mode === ObjectView.mode.MODE_3D) {
      siblingCamera = this.camera;
    } else if (mode === ObjectView.mode.MODE_2D) {
      if (this.options.orthographicCamera.position) {
        siblingCamera = this.options.axis;
      } else {
        siblingCamera = this.options.orthographicCamera;
      }
    }

    if (this.axis) {
      this.axis.setSiblingCamera(siblingCamera); 
    } else {
      this.axis = new Axis({
        width: 150,
        height: 150,
        siblingCamera,
        // orientation: mode === ObjectView.mode.MODE_3D ? this.options.axis.orientation3D : this.options.axis.orientation2D
      });
    }
    this.axis.setAxisOrientation(mode === ObjectView.mode.MODE_3D ? this.options.axis.orientation3D : this.options.axis.orientation2D);

    let wrapper = document.querySelector('#axis-wrapper');
    if (wrapper) {
      // VladP: Setting innerHTML to empty string does not work in IE11
      // wrapper.innerHTML = '';
      for (let i = 0; i < wrapper.children; i++) {
        wrapper.removeChild(wrapper.children[i]);
      }
    } else {
      wrapper = document.createElement('div');
      wrapper.id = 'axis-wrapper';
      wrapper.style.position = 'fixed';
      wrapper.style.left = '0';
      wrapper.style.bottom = '0';
      wrapper.style.zIndex = '100';
      wrapper.style.pointerEvents = 'none';
      this.renderer.domElement.parentNode.appendChild(wrapper);
    }

    wrapper.appendChild(this.axis.domElement);
  }

  updateAxis(options) {
    if (this.options.mode === ObjectView.mode.MODE_2D) {
      this.axis.setSiblingCamera(options);
    }
  }

  takeSnapshot(id, cb) {
    const type = 'image/jpeg';

    var gl = this.renderer.getContext('webgl') || this.renderer.getContext('experimental-webgl');
    
    var width = this.renderer.domElement.width;
    var height = this.renderer.domElement.height;

    var canvas = document.createElement('canvas');
    canvas.id = "Snapshot_" + id;
    canvas.width  = width;
    canvas.height = height;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext("2d");
    let prevZoom = this.camera.zoom;
    // this.animate();
    this.camera.zoom = 1.4; // increase the size of the text
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
    if (this.object) {
      this.object.render(this.camera, this.renderer, this.controls);
      this.addObject(this.object); // update this.htmlLayer
      this.renderer.renderLists.dispose();
    }
    ctx.drawImage(gl.canvas, 0, 0, width, height, 0, 0, width, height);
    this.camera.zoom = prevZoom;

    html2canvas(this.htmlLayer.domElement, { canvas: canvas, scale: 1 }).then(
      () => {
        cb(id, canvas.toDataURL(type).split(',')[1]);
        document.body.removeChild(canvas);
      },
      () => {
        callErrorNotification(APP_TEXTS.NOTIFICATION_ERROR_REPORT_SNAPSHOT.Content);
        cb(id, canvas.toDataURL(type).split(',')[1]);
        document.body.removeChild(canvas);
      }
    );
  
    // var data = //"data:image/svg+xml;charset=utf-8," + 
    // '<svg xmlns="http://www.w3.org/2000/svg" width="' + width + '" height="' + height + '">' +
    //   '<style type="text/css">body {font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;}</style>' +
    //   '<foreignObject width="100%" height="100%">' +
    //     this.html_to_xml(this.htmlLayer.domElement.innerHTML) +
    //   '</foreignObject>' +
    // '</svg>';

    // var img = new Image();
    // // img.setAttribute('crossOrigin', 'anonymous');
    // img.addEventListener("load", async function () {
    //   ctx.drawImage(img, 0, 0, width, height, 0, 0, width, height);
    //   cb(id, canvas.toDataURL(type).split(',')[1]);
    //   document.body.removeChild(canvas);
    // });

    // img.onerror = () => {
    //   document.body.removeChild(canvas);
    //   callErrorNotification(APP_TEXTS.NOTIFICATION_ERROR_REPORT_SNAPSHOT.Content);
    // };

    // img.src = svgToTinyDataUri(data);
  }

  html_to_xml(html) {
    var doc = document.implementation.createHTMLDocument('');
    doc.write(html);
  
    // You must manually set the xmlns if you intend to immediately serialize     
    // the HTML document to a string as opposed to appending it to a
    // <foreignObject> in the DOM
    if (doc.documentElement)
      doc.documentElement.setAttribute('xmlns', doc.documentElement.namespaceURI);
  
    var htmlSerialized = new XMLSerializer().serializeToString(doc.body);
    return htmlSerialized;
  }

  onDragControlEvent(event) {
    // event contains only the mesh and the draggable objects, shouldn't be necessary to pass to the implemented listeners
    let object = event.target.getObjects().find((obj) => obj.getMeshUuid() === event.object.uuid);
    if (object) {
      switch (event.type) {
        case 'dragstart': { object.onDragStart(); break;}
        case 'dragend':   { object.onDragEnd(); break;}
        case 'drag':      { object.onDrag(); break;}
        case 'hoveron':   { object.onHoverEnter(); break;}
        case 'hoveroff':  { object.onHoverExit(); break;}
        default:
          break;
      }
    }
  }

}



// WEBPACK FOOTER //
// ./src/object-view/views/object-view.js